import { Download } from 'lucide-react';
import { useState } from 'react';

import { useAmbientesControllerListar } from '@/api/generated/admin-ambientes/admin-ambientes';
import { useRelatoriosControllerPreferencias } from '@/api/generated/admin-relatorios/admin-relatorios';
import type { ModuloDePreferenciaResponse } from '@/api/generated/model';
import { ExportarCsvDialogo } from '@/shared/components/ExportarCsvDialogo';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Skeleton } from '@/shared/ui/skeleton';

const TODOS = 'todos';

/**
 * "Preferências" — ranking dos módulos pelo volume TOTAL de registros (DEC-7),
 * na base inteira ou no ambiente filtrado. É uma pergunta diferente da aba
 * "Uso por usuário": aqui é "onde o uso se concentra", não "quem mais usa".
 *
 * Duas ou mais seleções comparam entre si — vira gráfico de barras vertical.
 * Uma seleção não compara nada — vira a barra horizontal de magnitude (a
 * mesma linguagem visual do Funil de Discussões), medida contra o maior
 * módulo da base inteira, não contra si mesma (senão a barra sempre
 * apareceria 100% cheia, o que não diz nada).
 */
export function PreferenciasTab() {
  const { pode } = usePermissoes();
  const [ambienteId, setAmbienteId] = useState<string>(TODOS);
  // `null` = ainda não mexeu no seletor → mostra todos.
  const [selecionados, setSelecionados] = useState<Set<string> | null>(null);
  const [exportando, setExportando] = useState(false);

  const { data, isPending } = useRelatoriosControllerPreferencias({
    ...(ambienteId !== TODOS ? { tenantId: ambienteId } : {}),
  });

  const { data: ambientes } = useAmbientesControllerListar(
    { pagina: 1, porPagina: 100, ordenarPor: 'nome', ordem: 'asc' },
    { query: { enabled: pode('admin_tenants.visualizar') } },
  );

  function alternar(modulo: string) {
    setSelecionados((atual) => {
      const base = atual ?? new Set(data?.modulos.map((m) => m.modulo));
      const novo = new Set(base);
      if (novo.has(modulo)) novo.delete(modulo);
      else novo.add(modulo);
      return novo;
    });
  }

  const modulos = data?.modulos ?? [];
  const visiveis = selecionados ? modulos.filter((m) => selecionados.has(m.modulo)) : modulos;
  const maiorGeral = Math.max(1, ...modulos.map((m) => m.total));

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 flex-1">
          {ambientes && (
            <Select value={ambienteId} onValueChange={setAmbienteId}>
              <SelectTrigger className="sm:w-52" aria-label="Filtrar por ambiente">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TODOS}>Todos os ambientes</SelectItem>
                {ambientes.itens.map((ambiente) => (
                  <SelectItem key={ambiente.id} value={ambiente.id}>
                    {ambiente.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {modulos.length > 0 && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              {modulos.map((modulo) => {
                const marcado = selecionados ? selecionados.has(modulo.modulo) : true;
                return (
                  <label
                    key={modulo.modulo}
                    className="flex items-center gap-2 text-sm text-foreground cursor-pointer"
                  >
                    <Checkbox checked={marcado} onCheckedChange={() => alternar(modulo.modulo)} />
                    {modulo.label}
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <Button variant="outline" size="sm" className="gap-2" onClick={() => setExportando(true)}>
          <Download className="w-4 h-4" />
          Exportar
        </Button>
      </div>

      <div className="bg-surface-raised rounded-lg border-2 border-brand/20 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-brand/5 to-transparent border-b border-brand/10">
          <h3 className="text-sm font-semibold text-brand uppercase tracking-wide">
            Volume total por módulo
          </h3>
        </div>

        <div className="p-6">
          {isPending ? (
            <div className="space-y-4">
              {[0, 1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-10 rounded-md" />
              ))}
            </div>
          ) : visiveis.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              Selecione ao menos um módulo para ver o volume.
            </p>
          ) : visiveis.length === 1 ? (
            <BarraUnica modulo={visiveis[0]!} maior={maiorGeral} />
          ) : (
            <GraficoDeBarras modulos={visiveis} />
          )}
        </div>
      </div>

      <ExportarCsvDialogo
        aberto={exportando}
        aoFechar={() => setExportando(false)}
        titulo="Exportar preferências"
        caminho="/v1/admin/relatorios/preferencias/exportar.csv"
        descricaoCompleto="Exporta o volume total de todos os módulos, ignorando o filtro de ambiente — mesmo total que a aba mostra hoje."
        descricaoPeriodo="Aplica o filtro de ambiente e conta só os registros criados (discussões: abertas) no intervalo escolhido."
        filtros={{ tenantId: ambienteId !== TODOS ? ambienteId : undefined }}
      />
    </div>
  );
}

/** Um módulo só: sem nada para comparar, então vira a barra horizontal de magnitude — mesma linguagem do Funil de Discussões. */
function BarraUnica({ modulo, maior }: { modulo: ModuloDePreferenciaResponse; maior: number }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-sm font-medium text-foreground">{modulo.label}</span>
        <span className="text-sm font-semibold text-brand">{modulo.total}</span>
      </div>
      <div className="h-2 rounded-full bg-brand/10 overflow-hidden">
        <div
          className="h-full rounded-full bg-brand"
          style={{ width: `${(modulo.total / maior) * 100}%` }}
        />
      </div>
    </div>
  );
}

/**
 * Duas ou mais seleções: gráfico de barras vertical, altura relativa ao maior
 * ENTRE OS SELECIONADOS — é a comparação que a pessoa pediu ao selecionar
 * justamente este recorte, não a magnitude contra módulos que ela tirou da
 * vista.
 */
function GraficoDeBarras({ modulos }: { modulos: ModuloDePreferenciaResponse[] }) {
  const maior = Math.max(1, ...modulos.map((m) => m.total));

  return (
    <div className="flex items-end justify-center gap-6 h-56 border-b border-border px-4">
      {modulos.map((modulo) => (
        <div
          key={modulo.modulo}
          className="flex flex-col items-center justify-end h-full flex-1 max-w-28 group"
        >
          <span className="text-sm font-semibold text-brand mb-1">{modulo.total}</span>
          <div
            className="w-full max-w-16 rounded-t-md bg-brand transition-[height]"
            style={{ height: `${Math.max(2, (modulo.total / maior) * 100)}%` }}
            title={`${modulo.label}: ${modulo.total}`}
          />
          <span className="text-xs text-muted-foreground text-center mt-2 leading-tight">
            {modulo.label}
          </span>
        </div>
      ))}
    </div>
  );
}
